import { useState, useEffect, useCallback } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Mail, Send, RefreshCw, Trash2, Eye, EyeOff } from "lucide-react";

const EMPTY = {
  sender_name: "",
  sender_email: "",
  smtp_host: "",
  smtp_port: 587,
  smtp_username: "",
  smtp_password: "",
  use_tls: true,
};

/**
 * Per-market SMTP override panel. SystemAdmin-only.
 * Empty fields → falls back to the global SMTP. Password is masked when loaded
 * and only re-sent when the admin types a new value.
 */
export default function MarketSmtpPanel({ marketId }) {
  const [form, setForm] = useState(EMPTY);
  const [usesFallback, setUsesFallback] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const load = useCallback(async () => {
    if (!marketId) return;
    setLoading(true);
    try {
      const res = await api.get(`/admin/markets/${encodeURIComponent(marketId)}/smtp`);
      const { _uses_fallback, ...rest } = res.data || {};
      setForm({ ...EMPTY, ...rest });
      setUsesFallback(!!_uses_fallback);
    } catch {
      toast.error("Failed to load market SMTP settings");
    } finally {
      setLoading(false);
    }
  }, [marketId]);

  useEffect(() => { load(); }, [load]);

  const update = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/markets/${encodeURIComponent(marketId)}/smtp`, form);
      toast.success(`SMTP saved for ${marketId}`);
      // IMPORTANT: do NOT reload from server — that would replace the typed
      // password with the masked '********' sentinel and give the impression
      // that the password got cut off. The form already has what we just sent.
      // We only need to refresh the "uses fallback" indicator.
      const required = ["smtp_host", "smtp_port", "smtp_username", "smtp_password", "sender_email"];
      const isComplete = required.every((k) => form[k]);
      setUsesFallback(!isComplete);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save SMTP");
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setSaving(true);
    try {
      await api.delete(`/admin/markets/${encodeURIComponent(marketId)}/smtp`);
      toast.success("Cleared. Using global SMTP.");
      setForm(EMPTY);
      setUsesFallback(true);
    } catch {
      toast.error("Failed to clear SMTP");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await api.post(`/admin/markets/${encodeURIComponent(marketId)}/smtp/test`);
      toast.success(
        res.data.used_market_smtp
          ? `Test email sent via ${marketId} SMTP`
          : "Test email sent via global SMTP (no market override)"
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test email failed");
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card className="bg-white border-gray-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold text-gray-900 flex items-center gap-2">
          <Mail className="w-4 h-4 text-indigo-600" />
          SMTP for this Market
          {usesFallback ? (
            <span
              className="text-[11px] font-normal px-2 py-0.5 rounded-full bg-gray-100 text-gray-600"
              data-testid="smtp-uses-global-badge"
            >
              Using global SMTP
            </span>
          ) : (
            <span
              className="text-[11px] font-normal px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
              data-testid="smtp-uses-market-badge"
            >
              Custom SMTP active
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-gray-500">
          Override the email sender for this market. Leave any field blank to fall
          back to the global SMTP. Only emails for this market use these credentials.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Sender Name</Label>
            <Input
              value={form.sender_name || ""}
              onChange={(e) => update("sender_name", e.target.value)}
              placeholder="QNC Maintenance"
              data-testid="market-smtp-sender-name"
            />
          </div>
          <div>
            <Label className="text-xs">Sender Email</Label>
            <Input
              type="email"
              value={form.sender_email || ""}
              onChange={(e) => update("sender_email", e.target.value)}
              placeholder="qnc@yourcompany.com"
              data-testid="market-smtp-sender-email"
            />
          </div>
          <div>
            <Label className="text-xs">SMTP Host</Label>
            <Input
              value={form.smtp_host || ""}
              onChange={(e) => update("smtp_host", e.target.value)}
              placeholder="smtp.office365.com"
              data-testid="market-smtp-host"
            />
          </div>
          <div>
            <Label className="text-xs">SMTP Port</Label>
            <Input
              type="number"
              value={form.smtp_port || 587}
              onChange={(e) => update("smtp_port", parseInt(e.target.value) || 587)}
              data-testid="market-smtp-port"
            />
          </div>
          <div>
            <Label className="text-xs">SMTP Username</Label>
            <Input
              value={form.smtp_username || ""}
              onChange={(e) => update("smtp_username", e.target.value)}
              placeholder="qnc@yourcompany.com"
              data-testid="market-smtp-username"
            />
          </div>
          <div>
            <Label className="text-xs">SMTP Password</Label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={form.smtp_password || ""}
                onChange={(e) => update("smtp_password", e.target.value)}
                placeholder="App password"
                className="pr-20 font-mono"
                data-testid="market-smtp-password"
                autoComplete="off"
                spellCheck={false}
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                {form.smtp_password === "********" && (
                  <button
                    type="button"
                    onClick={() => {
                      update("smtp_password", "");
                      setShowPassword(true);
                    }}
                    className="text-[11px] px-2 py-0.5 rounded bg-amber-100 text-amber-800 hover:bg-amber-200 font-medium"
                    title="Clear and type a new password"
                    data-testid="market-smtp-password-replace"
                  >
                    Replace
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="text-gray-400 hover:text-gray-700"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  data-testid="market-smtp-password-toggle"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {form.smtp_password === "********" ? (
              <p className="text-[11px] text-gray-500 mt-1 flex items-center gap-1">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
                A password is saved (hidden for security). Click{" "}
                <span className="font-medium text-amber-700">Replace</span> to enter a new one.
              </p>
            ) : form.smtp_password ? (
              <p className="text-[11px] text-gray-500 mt-1">
                Length: <span className="font-mono">{form.smtp_password.length}</span> characters
                {/^\s|\s$/.test(form.smtp_password) && (
                  <span className="ml-2 text-amber-600 font-medium">⚠ has leading/trailing spaces</span>
                )}
              </p>
            ) : null}
          </div>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <Switch
              checked={!!form.use_tls}
              onCheckedChange={(v) => update("use_tls", v)}
              data-testid="market-smtp-tls"
            />
            <span className="text-xs text-gray-600">Use STARTTLS</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          <Button
            onClick={handleSave}
            disabled={saving || loading}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="market-smtp-save"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-2" />
            {saving ? "Saving..." : "Save"}
          </Button>
          <Button
            variant="outline"
            onClick={handleTest}
            disabled={testing || loading}
            data-testid="market-smtp-test"
          >
            <Send className="w-3.5 h-3.5 mr-2" />
            {testing ? "Sending..." : "Send Test Email"}
          </Button>
          {!usesFallback && (
            <Button
              variant="ghost"
              onClick={handleClear}
              disabled={saving || loading}
              className="text-red-600 hover:bg-red-50 hover:text-red-700"
              data-testid="market-smtp-clear"
            >
              <Trash2 className="w-3.5 h-3.5 mr-2" />
              Clear &amp; Use Global
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
